import type { ConsoleRole } from '@paguz/contracts';
import {
  ArrowLeftRightIcon,
  ScaleIcon,
  BookOpenIcon,
  BotIcon,
  ClipboardListIcon,
  SparklesIcon,
  FolderKanbanIcon,
  FlaskConicalIcon,
  LayoutDashboardIcon,
  ScrollTextIcon,
  SlidersHorizontalIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';
import { can, type Permission } from '../lib/rbac.ts';

export interface NavItem {
  to:
    | '/'
    | '/expedientes'
    | '/transacciones'
    | '/clientes'
    | '/postventa'
    | '/agentes'
    | '/tareas'
    | '/conocimiento'
    | '/skills'
    | '/configuracion'
    | '/auditoria'
    | '/demo'
    | '/guia';
  label: string;
  icon: LucideIcon;
  permission: Permission;
  group: 'Operación' | 'Agentes IA' | 'Administración' | 'Demo';
}

/** Navegación lateral: cada sección se muestra solo si el rol tiene el permiso (§3.1). */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/',
    label: 'Inicio',
    icon: LayoutDashboardIcon,
    permission: 'dashboard:read',
    group: 'Operación',
  },
  {
    to: '/expedientes',
    label: 'Expedientes',
    icon: FolderKanbanIcon,
    permission: 'applications:read',
    group: 'Operación',
  },
  {
    to: '/transacciones',
    label: 'Transacciones',
    icon: ArrowLeftRightIcon,
    permission: 'transactions:read',
    group: 'Operación',
  },
  {
    to: '/clientes',
    label: 'Clientes y líneas',
    icon: UsersIcon,
    permission: 'customers:read',
    group: 'Operación',
  },
  {
    to: '/postventa',
    label: 'Disputas y conciliación',
    icon: ScaleIcon,
    permission: 'disputes:read',
    group: 'Operación',
  },
  {
    to: '/agentes',
    label: 'Sesiones y trazas',
    icon: BotIcon,
    permission: 'agents:read',
    group: 'Agentes IA',
  },
  {
    to: '/tareas',
    label: 'Tareas HITL',
    icon: ClipboardListIcon,
    permission: 'hitl:read',
    group: 'Agentes IA',
  },
  {
    to: '/conocimiento',
    label: 'Base de conocimiento',
    icon: BookOpenIcon,
    permission: 'knowledge:read',
    group: 'Agentes IA',
  },
  {
    to: '/skills',
    label: 'Skills',
    icon: SparklesIcon,
    permission: 'knowledge:read',
    group: 'Agentes IA',
  },
  {
    to: '/configuracion',
    label: 'Configuración',
    icon: SlidersHorizontalIcon,
    permission: 'config:read',
    group: 'Administración',
  },
  {
    to: '/auditoria',
    label: 'Auditoría',
    icon: ScrollTextIcon,
    permission: 'audit:read',
    group: 'Administración',
  },
  {
    to: '/demo',
    label: 'Controles de demo',
    icon: FlaskConicalIcon,
    permission: 'demo:operate',
    group: 'Demo',
  },
  {
    to: '/guia',
    label: 'Guía del demo',
    icon: BookOpenIcon,
    permission: 'dashboard:read',
    group: 'Demo',
  },
];

export function navFor(roles: readonly ConsoleRole[]): NavItem[] {
  return NAV_ITEMS.filter((item) => can(roles, item.permission));
}

/** Destino de búsqueda global según el prefijo del ID (§2: `pi_…`, `ln_…`, `cus_…`, documento, celular). */
export type SearchTarget =
  | { kind: 'payment'; id: string }
  | { kind: 'line'; id: string }
  | { kind: 'customer'; id: string }
  | { kind: 'application'; id: string }
  | { kind: 'customers'; q: string };

export function resolveSearch(raw: string): SearchTarget | null {
  const q = raw.trim();
  if (!q) return null;
  if (/^pi_/i.test(q)) return { kind: 'payment', id: q };
  if (/^ln_/i.test(q)) return { kind: 'line', id: q };
  if (/^cus_/i.test(q)) return { kind: 'customer', id: q };
  if (/^app_/i.test(q)) return { kind: 'application', id: q };
  return { kind: 'customers', q };
}
